import React, { useState } from 'react';

interface WorkOrder {
  id: number;
  title: string;
  client: string;
  address: string;
  type: 'installation' | 'repair' | 'maintenance' | 'inspection';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'in-progress' | 'completed' | 'cancelled';
  assignedTo: string;
  scheduledDate: string;
  estimatedHours: number;
  cost: string;
  description: string;
}

const WorkOrdersPage: React.FC = () => {
  const [workOrders] = useState<WorkOrder[]>([
    {
      id: 1,
      title: 'AC Installation - Residential',
      client: 'John Smith',
      address: '123 Main St, Anytown',
      type: 'installation',
      priority: 'high',
      status: 'pending',
      assignedTo: 'Mike Johnson',
      scheduledDate: '2024-01-16',
      estimatedHours: 6,
      cost: '$2,500',
      description: 'Install new central air conditioning system'
    },
    {
      id: 2,
      title: 'Furnace Repair - Emergency',
      client: 'Sarah Wilson',
      address: '456 Oak Ave, Downtown',
      type: 'repair',
      priority: 'urgent',
      status: 'in-progress',
      assignedTo: 'David Lee',
      scheduledDate: '2024-01-15',
      estimatedHours: 3,
      cost: '$850',
      description: 'Furnace not heating, needs immediate attention'
    },
    {
      id: 3,
      title: 'Quarterly Maintenance',
      client: 'ABC Corporation',
      address: '789 Business Blvd, Commercial District',
      type: 'maintenance',
      priority: 'medium',
      status: 'completed',
      assignedTo: 'Tom Anderson',
      scheduledDate: '2024-01-12',
      estimatedHours: 4,
      cost: '$400',
      description: 'Routine quarterly HVAC system maintenance'
    },
    {
      id: 4,
      title: 'Heat Pump Installation',
      client: 'Lisa Davis',
      address: '321 Pine Rd, Suburbs',
      type: 'installation',
      priority: 'medium',
      status: 'pending',
      assignedTo: 'Chris Miller',
      scheduledDate: '2024-01-18',
      estimatedHours: 8,
      cost: '$4,200',
      description: 'Install new heat pump system with ductwork modifications'
    },
    {
      id: 5,
      title: 'System Inspection',
      client: 'Robert Brown',
      address: '654 Elm St, Neighborhood',
      type: 'inspection',
      priority: 'low',
      status: 'pending',
      assignedTo: 'Steve Wilson',
      scheduledDate: '2024-01-20',
      estimatedHours: 2,
      cost: '$150',
      description: 'Annual HVAC system inspection and safety check'
    }
  ]);

  const [showAddForm, setShowAddForm] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return '#ffc107';
      case 'in-progress': return '#17a2b8';
      case 'completed': return '#28a745';
      case 'cancelled': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return '#dc3545';
      case 'high': return '#fd7e14';
      case 'medium': return '#ffc107';
      case 'low': return '#28a745';
      default: return '#6c757d';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'installation': return '🔧';
      case 'repair': return '⚡';
      case 'maintenance': return '🔄';
      case 'inspection': return '🔍';
      default: return '📋';
    }
  };

  const filteredWorkOrders = workOrders.filter(wo => {
    const statusMatch = filterStatus === 'all' || wo.status === filterStatus;
    const typeMatch = filterType === 'all' || wo.type === filterType;
    return statusMatch && typeMatch;
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div>
          <h1 style={{ color: '#2c3e50', marginBottom: '10px' }}>Work Orders</h1>
          <p style={{ color: '#6c757d' }}>Manage installation, repair, and maintenance tasks</p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => setShowAddForm(true)}
        >
          + Create Work Order
        </button>
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <label style={{ marginRight: '10px', fontWeight: '500' }}>Status:</label>
            <select 
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <label style={{ marginRight: '10px', fontWeight: '500' }}>Type:</label>
            <select 
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="all">All Types</option>
              <option value="installation">Installation</option>
              <option value="repair">Repair</option>
              <option value="maintenance">Maintenance</option>
              <option value="inspection">Inspection</option>
            </select>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <strong>Total Work Orders: {filteredWorkOrders.length}</strong>
          </div>
        </div>
      </div>

      {showAddForm && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>Create New Work Order</h3>
          <form style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
            <div className="form-group">
              <label>Title *</label>
              <input type="text" required placeholder="Work order title" />
            </div>
            <div className="form-group">
              <label>Client *</label>
              <input type="text" required placeholder="Client name" />
            </div>
            <div className="form-group">
              <label>Type *</label>
              <select required>
                <option value="">Select Type</option>
                <option value="installation">Installation</option>
                <option value="repair">Repair</option>
                <option value="maintenance">Maintenance</option>
                <option value="inspection">Inspection</option>
              </select>
            </div>
            <div className="form-group">
              <label>Priority *</label>
              <select required>
                <option value="">Select Priority</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="form-group">
              <label>Assigned To</label>
              <select>
                <option value="">Select Technician</option>
                <option value="Mike Johnson">Mike Johnson</option>
                <option value="David Lee">David Lee</option>
                <option value="Tom Anderson">Tom Anderson</option>
                <option value="Chris Miller">Chris Miller</option>
                <option value="Steve Wilson">Steve Wilson</option>
              </select>
            </div>
            <div className="form-group">
              <label>Scheduled Date</label>
              <input type="date" />
            </div>
            <div className="form-group">
              <label>Estimated Hours</label>
              <input type="number" placeholder="0" />
            </div>
            <div className="form-group">
              <label>Estimated Cost</label>
              <input type="text" placeholder="$0" />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Address</label>
              <input type="text" placeholder="Service address" />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Description</label>
              <textarea 
                rows={3} 
                placeholder="Detailed description of work to be performed"
                style={{ 
                  width: '100%', 
                  padding: '10px', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  resize: 'vertical'
                }}
              ></textarea>
            </div>
          </form>
          <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
            <button className="btn btn-primary">Create Work Order</button>
            <button 
              className="btn btn-secondary"
              onClick={() => setShowAddForm(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <table className="data-table">
          <thead>
            <tr>
              <th>Work Order</th>
              <th>Client & Address</th>
              <th>Type & Priority</th>
              <th>Assigned To</th>
              <th>Schedule</th>
              <th>Status</th>
              <th>Cost</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredWorkOrders.map((wo) => (
              <tr key={wo.id}>
                <td>
                  <div>
                    <strong>#{wo.id} - {wo.title}</strong>
                    <br />
                    <small style={{ color: '#6c757d' }}>{wo.description}</small>
                  </div>
                </td>
                <td>
                  <div>
                    <strong>{wo.client}</strong>
                    <br />
                    <small style={{ color: '#6c757d' }}>{wo.address}</small>
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      {getTypeIcon(wo.type)} {wo.type}
                    </div>
                    <span 
                      className="status"
                      style={{ 
                        backgroundColor: `${getPriorityColor(wo.priority)}20`,
                        color: getPriorityColor(wo.priority),
                        fontSize: '11px'
                      }}
                    >
                      {wo.priority}
                    </span>
                  </div>
                </td>
                <td>{wo.assignedTo}</td>
                <td>
                  <div>
                    {wo.scheduledDate}
                    <br />
                    <small style={{ color: '#6c757d' }}>{wo.estimatedHours}h estimated</small>
                  </div>
                </td>
                <td>
                  <span 
                    className="status"
                    style={{ 
                      backgroundColor: `${getStatusColor(wo.status)}20`,
                      color: getStatusColor(wo.status)
                    }}
                  >
                    {wo.status}
                  </span>
                </td>
                <td><strong>{wo.cost}</strong></td>
                <td>
                  <div style={{ display: 'flex', gap: '5px', flexDirection: 'column' }}>
                    <button 
                      style={{ 
                        padding: '4px 8px', 
                        fontSize: '11px', 
                        backgroundColor: '#007bff', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '3px',
                        cursor: 'pointer'
                      }}
                    >
                      Edit
                    </button>
                    <button 
                      style={{ 
                        padding: '4px 8px', 
                        fontSize: '11px', 
                        backgroundColor: '#28a745', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '3px',
                        cursor: 'pointer'
                      }}
                    >
                      Complete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WorkOrdersPage;